import type { PermissionKey, Role } from '@obras/shared';
import { Link, useMatches, useRouterState } from '@tanstack/react-router';
import {
  Building,
  ChartLine,
  HardDrive,
  HardHat,
  Landmark,
  LayoutDashboard,
  ListChecks,
  Map as MapIcon,
  ScrollText,
  Settings,
  ShieldCheck,
  Users,
} from 'lucide';
import {
  ChevronLeft,
  ChevronRight,
  LogOut,
  Menu,
  Moon,
  Repeat,
  Sun,
  UserRound,
  X,
  type LucideIcon,
} from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';

import { usePainel } from '../features/painel/api.js';
import { useAuth } from '../lib/auth.js';
import { cn } from '../lib/cn.js';
import { useStoredState } from '../lib/storage.js';
import { useTheme } from '../lib/theme.js';
import { AnimatedIcon, type IconNode } from './AnimatedIcon.js';
import { BrandLockup, BrandMark } from './Brand.js';
import { EASE_OUT_QUINT, PageTransition, TRANSITION } from './motion.js';
import { TechText } from './TechText.js';
import { Avatar } from './ui/Feedback.js';

const ICON = { strokeWidth: 1.5 } as const;

/** Largura do trilho aberto e recolhido. */
const RAIL_WIDTH = 256;
const RAIL_WIDTH_COLLAPSED = 68;

/** Os dois números que o trilho mostra, vindos da leitura do painel. */
type CountKey = 'pendencias' | 'obras';

interface NavItem {
  readonly to: string;
  readonly label: string;
  /** Nó do Lucide, não o componente: é o que permite animar traço a traço. */
  readonly icon: IconNode;
  /** Contador à direita do rótulo; `pendencias` leva o ponto laranja. */
  readonly count?: CountKey | undefined;
  /**
   * Some do menu para quem não alcança o papel ou não tem a permissão.
   *
   * É conforto visual, não segurança: a permissão de verdade é verificada no
   * servidor a cada requisição.
   */
  readonly minimumRole?: Role | undefined;
  readonly permission?: PermissionKey | undefined;
}

interface NavGroup {
  readonly label: string;
  readonly items: readonly NavItem[];
}

/** Menu do painel de um órgão. */
const ORGANIZATION_NAVIGATION: readonly NavGroup[] = [
  {
    label: 'Principal',
    items: [
      { to: '/', label: 'Painel', icon: LayoutDashboard, count: 'pendencias' },
      { to: '/obras', label: 'Obras', icon: HardHat, count: 'obras' },
      { to: '/mapa', label: 'Mapa de Obras', icon: MapIcon },
      { to: '/contratos', label: 'Contratos', icon: ScrollText },
      {
        to: '/tarefas',
        label: 'Tarefas',
        icon: ListChecks,
        permission: 'canAccessTasks',
      },
    ],
  },
  {
    label: 'Cadastros',
    items: [
      { to: '/empresas', label: 'Empresas', icon: Building },
      {
        to: '/secretarias',
        label: 'Secretarias',
        icon: Landmark,
        permission: 'canAccessSecretarias',
      },
      { to: '/usuarios', label: 'Usuários', icon: Users, minimumRole: 'gestor' },
    ],
  },
  {
    label: 'Gestão',
    items: [
      {
        to: '/relatorios',
        label: 'Relatórios',
        icon: ChartLine,
        permission: 'canAccessReports',
      },
      {
        to: '/auditoria',
        label: 'Auditoria',
        icon: ShieldCheck,
        permission: 'canAccessAudit',
      },
      { to: '/consumo', label: 'Consumo', icon: HardDrive, minimumRole: 'gestor' },
      { to: '/configuracoes', label: 'Configurações', icon: Settings, minimumRole: 'gestor' },
    ],
  },
];

/** Menu da área da plataforma. Só organizações e pessoas — nada operacional. */
const PLATFORM_NAVIGATION: readonly NavGroup[] = [
  {
    label: 'Plataforma',
    items: [
      { to: '/plataforma', label: 'Organizações', icon: Building },
      { to: '/plataforma/usuarios', label: 'Usuários', icon: Users },
    ],
  },
];

function isActive(currentPath: string, target: string): boolean {
  if (target === '/' || target === '/plataforma') return currentPath === target;
  return currentPath === target || currentPath.startsWith(`${target}/`);
}

/**
 * Os contadores do trilho vêm da mesma leitura do painel — a mesma chave de
 * cache, então a tela inicial e o menu nunca discordam. Fora de um órgão não
 * há painel, e a consulta fica desligada.
 */
function useNavCounts(enabled: boolean): Partial<Record<CountKey, number>> {
  const { data } = usePainel({ enabled });
  if (!data) return {};
  return { pendencias: data.attention.length, obras: data.totals.obras };
}

// ───────────────────────────────────────────────────────────── dica

/**
 * Rótulo do item com o trilho recolhido.
 *
 * Vai por portal, com posição fixa: o trilho rola na vertical, e um elemento
 * absoluto à direita de um item seria cortado pelo próprio rolo.
 */
function RailTip({
  anchorRef,
  label,
  visible,
}: {
  anchorRef: RefObject<HTMLElement | null>;
  label: string;
  visible: boolean;
}) {
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    if (!visible) return;
    const box = anchorRef.current?.getBoundingClientRect();
    if (box) setPosition({ top: box.top + box.height / 2, left: box.right + 10 });
  }, [visible, anchorRef]);

  return createPortal(
    <AnimatePresence>
      {visible && position ? (
        <motion.span
          role="tooltip"
          initial={{ opacity: 0, x: -4, y: '-50%' }}
          animate={{ opacity: 1, x: 0, y: '-50%' }}
          exit={{ opacity: 0, x: -4, y: '-50%' }}
          transition={TRANSITION.fast}
          style={{ position: 'fixed', top: position.top, left: position.left }}
          className="pointer-events-none z-50 rounded-sm border border-line bg-surface px-2 py-1 text-[12px] font-medium whitespace-nowrap text-ink shadow-pop"
        >
          {label}
        </motion.span>
      ) : null}
    </AnimatePresence>,
    document.body,
  );
}

// ───────────────────────────────────────────────────────────── item

/** A faixa do item começa na borda esquerda do trilho (`-left-3` desfaz o `px-3` do menu). */
const BAND = 'absolute inset-y-0 -left-3 right-0';

/** Hover: barra de 2px e degradê, em cinza. */
const HOVER_BAND = `${BAND} border-l-2 border-rail-muted bg-linear-to-r from-rail-2 to-transparent`;

/** A barra do ativo: 2px laranja, colada na borda esquerda do trilho. */
const BAR = 'absolute inset-y-0 -left-3 w-0.5 bg-primary';

/*
 * Seleção, em dois tempos. Primeiro a barra **cresce do meio para as duas
 * pontas** — `scaleY` de 0 a 1 com origem no centro —, em 280ms; só então a
 * faixa, num laranja tênue, **varre** da esquerda para a direita. Os dois
 * elementos montam quando o item vira ativo, e é a montagem que dispara a
 * sequência: o `initial` de cada um é o estado recolhido.
 */
const GROW = {
  scaleY: 1,
  transition: { duration: 0.28, ease: EASE_OUT_QUINT },
};

const SWEEP = {
  scaleX: 1,
  transition: { delay: 0.28, duration: 0.4, ease: EASE_OUT_QUINT },
};

/**
 * Um item do trilho.
 *
 * O ícone responde ao ponteiro redesenhando os traços (ver `AnimatedIcon`); o
 * estado é guardado aqui e passado como variante, para que o foco do teclado
 * dispare o mesmo desenho que o ponteiro.
 */
function NavLink({
  item,
  active,
  collapsed,
  count,
  onNavigate,
}: {
  item: NavItem;
  active: boolean;
  collapsed: boolean;
  count: number | undefined;
  onNavigate?: (() => void) | undefined;
}) {
  const [hovered, setHovered] = useState(false);
  const ref = useRef<HTMLLIElement>(null);
  const dot = item.count === 'pendencias' && count !== undefined && count > 0;
  const state = hovered ? 'hover' : active ? 'active' : 'rest';

  return (
    <li
      ref={ref}
      className="relative"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <Link
        to={item.to}
        onClick={onNavigate}
        onFocus={() => setHovered(true)}
        onBlur={() => setHovered(false)}
        aria-current={active ? 'page' : undefined}
        aria-label={collapsed ? item.label : undefined}
        className={cn(
          'relative flex h-9 w-full items-center text-[14px] transition-colors duration-100',
          collapsed ? 'justify-center' : 'px-2.5',
          active ? 'text-rail-ink' : 'text-rail-muted hover:text-rail-ink',
        )}
      >
        {/*
          A faixa: uma barra de 2px colada na borda esquerda do trilho — por
          isso sai do item e invade o respiro do menu — e um degradê que se
          apaga para a direita. Em cinza sob o ponteiro (e sob o foco do
          teclado), surgindo em 200ms; em laranja no ativo, com a barra
          crescendo do meio e a varredura da faixa.
        */}
        {active ? (
          <>
            <motion.span
              aria-hidden
              initial={{ scaleY: 0 }}
              animate={GROW}
              style={{ transformOrigin: 'center' }}
              className={BAR}
            />
            <motion.span
              aria-hidden
              initial={{ scaleX: 0 }}
              animate={SWEEP}
              style={{ transformOrigin: 'left center' }}
              className={cn(BAND, 'bg-linear-to-r from-primary/15 to-transparent')}
            />
          </>
        ) : (
          <span
            aria-hidden
            className={cn(
              HOVER_BAND,
              'transition-opacity duration-200',
              hovered ? 'opacity-100' : 'opacity-0',
            )}
          />
        )}

        <motion.span
          initial="rest"
          animate={state}
          className={cn('relative z-10 flex min-w-0 items-center gap-2.5', !collapsed && 'flex-1')}
        >
          <AnimatedIcon node={item.icon} className={active ? 'text-primary' : undefined} />

          {collapsed ? null : (
            <>
              <span className="min-w-0 flex-1 truncate">{item.label}</span>
              {dot ? <span className="size-1.5 rounded-full bg-primary" aria-hidden /> : null}
              {count === undefined ? null : (
                <span
                  className={cn(
                    'tabular text-[12px]',
                    dot ? 'font-semibold text-rail-ink' : 'text-rail-muted',
                  )}
                >
                  {count}
                </span>
              )}
            </>
          )}
        </motion.span>

        {collapsed && dot ? (
          <span className="absolute top-2 right-2 size-1.5 rounded-full bg-primary" aria-hidden />
        ) : null}
      </Link>

      {collapsed ? <RailTip anchorRef={ref} label={item.label} visible={hovered} /> : null}
    </li>
  );
}

function SidebarNav({
  collapsed = false,
  onNavigate,
}: {
  collapsed?: boolean | undefined;
  onNavigate?: (() => void) | undefined;
}) {
  const currentPath = useRouterState({ select: (state) => state.location.pathname });
  const { can, may, inPlatform } = useAuth();
  const counts = useNavCounts(!inPlatform);

  const groups = (inPlatform ? PLATFORM_NAVIGATION : ORGANIZATION_NAVIGATION)
    .map((group) => ({
      ...group,
      items: group.items.filter(
        (item) =>
          (!item.minimumRole || can(item.minimumRole)) &&
          (!item.permission || may(item.permission)),
      ),
    }))
    .filter((group) => group.items.length > 0);

  return (
    <nav className="flex flex-col gap-5 px-3 py-3" aria-label="Navegação principal">
      {groups.map((group, index) => (
        <div key={group.label}>
          {/*
            O divisor de grupo: um quadradinho em cada ponta, uma linha que
            para antes do rótulo, o rótulo centrado em versalete, e a linha
            seguindo até o outro quadradinho. Recolhido, só a linha entre os
            quadradinhos — o rótulo vai para a dica de cada item.
          */}
          {collapsed ? (
            index > 0 ? (
              <div className="mb-3 flex items-center px-1" aria-hidden>
                <span className="size-[3px] shrink-0 bg-rail-muted/70" />
                <span className="h-px flex-1 bg-rail-line" />
                <span className="size-[3px] shrink-0 bg-rail-muted/70" />
              </div>
            ) : null
          ) : (
            <div className="mb-2 flex items-center px-0.5">
              <span aria-hidden className="size-[3px] shrink-0 bg-rail-muted/70" />
              <span aria-hidden className="h-px flex-1 bg-rail-line" />
              <span className="shrink-0 px-2 text-[11px] font-medium tracking-[0.08em] text-rail-muted uppercase">
                {group.label}
              </span>
              <span aria-hidden className="h-px flex-1 bg-rail-line" />
              <span aria-hidden className="size-[3px] shrink-0 bg-rail-muted/70" />
            </div>
          )}

          <ul className="flex flex-col gap-0.5">
            {group.items.map((item) => (
              <NavLink
                key={item.to}
                item={item}
                active={isActive(currentPath, item.to)}
                collapsed={collapsed}
                count={item.count ? counts[item.count] : undefined}
                onNavigate={onNavigate}
              />
            ))}
          </ul>
        </div>
      ))}
    </nav>
  );
}

// ───────────────────────────────────────────────────────────── conta

const THEMES: readonly { value: 'light' | 'dark'; label: string; icon: LucideIcon }[] = [
  { value: 'light', label: 'Tema claro', icon: Sun },
  { value: 'dark', label: 'Tema escuro', icon: Moon },
];

/**
 * Claro ou escuro, na própria área do usuário: dois ícones num trilho redondo,
 * com o escolhido marcado por uma pílula que desliza e pelo ícone em laranja
 * — a pílula sozinha, cinza sobre cinza, não dizia de longe qual era qual.
 * Sem "sistema" como opção: quem nunca escolheu segue o aparelho, e isso
 * basta. A escolha fica no navegador, por aparelho, que é onde a preferência
 * de luz faz sentido.
 */
function ThemeToggle({
  orientation = 'horizontal',
}: {
  orientation?: 'horizontal' | 'vertical' | undefined;
}) {
  const { resolved, setPreference } = useTheme();
  const id = useId();

  return (
    <div
      role="radiogroup"
      aria-label="Tema"
      className={cn(
        'flex shrink-0 gap-0.5 rounded-full bg-rail-2 p-0.5',
        orientation === 'vertical' && 'flex-col',
      )}
    >
      {THEMES.map((theme) => {
        const selected = resolved === theme.value;
        return (
          <button
            key={theme.value}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={theme.label}
            title={theme.label}
            onClick={() => setPreference(theme.value)}
            className={cn(
              'relative grid size-7 place-items-center rounded-full transition-colors',
              selected ? 'text-primary' : 'text-rail-muted hover:text-rail-ink',
            )}
          >
            {selected ? (
              <motion.span
                layoutId={`${id}-tema`}
                transition={TRANSITION.layout}
                className="absolute inset-0 rounded-full bg-rail-line"
                aria-hidden
              />
            ) : null}
            {/*
              O ativo vai preenchido: só o traço, fino, deixava o laranja
              parecendo outro tom. Cheio, é o mesmo laranja do item ativo.
            */}
            <theme.icon
              size={15}
              strokeWidth={1.75}
              fill={selected ? 'currentColor' : 'none'}
              className="relative z-10"
              aria-hidden
            />
          </button>
        );
      })}
    </div>
  );
}

const MENU_ITEM =
  'flex w-full items-center gap-2.5 rounded-sm px-2.5 py-2 text-left text-[13px] text-ink transition-colors hover:bg-sunken';

/**
 * Conta do usuário: troca de ambiente, perfil e saída. O tema não fica aqui —
 * está na área do usuário, à vista (`ThemeToggle`).
 *
 * O painel é uma superfície clara com filete e a sombra de destaque, mesmo
 * dentro do trilho escuro: é o único lugar do trilho onde se lê um menu, e
 * um menu escuro sobre fundo escuro não teria onde terminar.
 */
function AccountMenu({ placement }: { placement: 'rail' | 'rail-collapsed' | 'topbar' }) {
  const { user, organization, environments, logout, switchEnvironment, inPlatform } = useAuth();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('mousedown', onPointerDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!user) return null;

  const compact = placement !== 'rail';
  const onDark = placement !== 'topbar';
  const currentEnvironment = inPlatform
    ? 'Administração da plataforma'
    : (organization?.name ?? '');
  const others = environments.filter((environment) =>
    inPlatform ? environment.kind !== 'platform' : environment.name !== organization?.name,
  );

  const panelPlacement = {
    rail: 'bottom-full left-0 mb-2 origin-bottom-left',
    'rail-collapsed': 'bottom-0 left-full ml-3 origin-bottom-left',
    topbar: 'top-full right-0 mt-2 origin-top-right',
  }[placement];

  const offset = placement === 'topbar' ? -4 : 4;

  return (
    <div ref={containerRef} className={cn('relative', !compact && 'min-w-0 flex-1')}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="Conta"
        className={cn(
          'flex items-center gap-2.5 rounded-md text-left transition-colors',
          onDark ? 'hover:bg-rail-2' : 'hover:bg-sunken',
          compact ? 'size-10 justify-center' : 'h-12 w-full px-2',
        )}
      >
        <Avatar name={user.name} url={user.avatarUrl} size={compact ? 28 : 32} onDark={onDark} />
        {compact ? null : (
          <span className="min-w-0 flex-1 leading-tight">
            <span className="block truncate text-[13px] font-medium text-rail-ink">
              {user.name}
            </span>
            {/* O órgão em tinta quase cheia: é onde a pessoa está, e se lê de longe. */}
            <span className="block truncate text-[12px] text-rail-ink/80">
              {currentEnvironment}
            </span>
          </span>
        )}
      </button>

      <AnimatePresence>
        {open ? (
          <motion.div
            role="menu"
            initial={{ opacity: 0, scale: 0.96, y: offset }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: offset }}
            transition={TRANSITION.fast}
            className={cn(
              'on-light absolute z-50 w-64 rounded-lg border border-line bg-surface p-1 text-ink shadow-pop',
              panelPlacement,
            )}
          >
            <div className="px-2.5 py-2">
              <p className="truncate text-[13px] font-medium">{user.name}</p>
              <p className="truncate font-mono text-[11px] text-ink-3">{user.email}</p>
            </div>

            {others.length > 0 ? (
              <>
                <p className="px-2.5 pt-2 pb-1 text-[11px] text-ink-3">Trocar de ambiente</p>
                {others.map((environment) => (
                  <button
                    key={environment.id}
                    type="button"
                    role="menuitem"
                    onClick={() => switchEnvironment(environment.id)}
                    className={MENU_ITEM}
                  >
                    <Repeat {...ICON} size={15} className="text-ink-3" aria-hidden />
                    <span className="truncate">{environment.name}</span>
                  </button>
                ))}
                <div className="my-1 h-px bg-line" aria-hidden />
              </>
            ) : null}

            <Link to="/perfil" role="menuitem" onClick={() => setOpen(false)} className={MENU_ITEM}>
              <UserRound {...ICON} size={15} className="text-ink-3" aria-hidden />
              Perfil
            </Link>

            <div className="my-1 h-px bg-line" aria-hidden />

            <button
              type="button"
              role="menuitem"
              onClick={() => void logout()}
              className={MENU_ITEM}
            >
              <LogOut {...ICON} size={15} className="text-ink-3" aria-hidden />
              Sair
            </button>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

// ───────────────────────────────────────────────────────────── trilho

/**
 * Alça de recolher e expandir: só o chevron, **ao lado** do trilho — fora
 * dele, 8px à direita da borda, na altura do cabeçalho, sobre a página. Fica
 * no mesmo lugar nos dois estados, que é o que faz a pessoa saber onde
 * procurá-la sem olhar; como é filha do trilho, acompanha a largura dele
 * durante a animação (`left-full`).
 *
 * Sem fundo, sem filete e sem sombra, de propósito: é um comando de ajuste,
 * usado uma vez e esquecido, e não pode disputar atenção com o menu. Está
 * sobre a página, então vai em `ink-3` e clareia para `ink` sob o ponteiro;
 * `on-light` devolve o anel de foco da página, já que o trilho em volta é
 * `on-dark`. A área de clique é de 28px — o alvo não precisa ser visível para
 * ser alcançável. Cabe na folga entre o trilho e o conteúdo (12px de folga mais
 * 32px de respiro da página), sem encostar em nada.
 */
function RailHandle({ collapsed, onToggle }: { collapsed: boolean; onToggle: () => void }) {
  const Icon = collapsed ? ChevronRight : ChevronLeft;
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={collapsed ? 'Expandir menu' : 'Recolher menu'}
      aria-expanded={!collapsed}
      className="on-light absolute top-[26px] left-full z-10 ml-1 grid size-7 place-items-center rounded-full text-ink-3 transition-colors hover:text-ink"
    >
      <Icon size={16} strokeWidth={1.75} aria-hidden />
    </button>
  );
}

function RailHeader({
  collapsed,
  home,
  onClose,
}: {
  collapsed: boolean;
  home: string;
  onClose?: (() => void) | undefined;
}) {
  /*
   * `overflow-hidden` no cabeçalho: ao expandir, o nome monta enquanto o
   * trilho ainda está estreito, e sem o corte ele vazaria para fora do
   * painel durante a animação.
   */
  return (
    <div className="flex h-20 shrink-0 items-center gap-2 overflow-hidden px-3">
      {/*
        Aberto, o cabeçalho é só o nome do sistema, centrado; recolhido, só a
        marca — também centrada. `mx-auto` no filho do flex faz os dois.
      */}
      <Link
        to={home}
        aria-label="Início"
        className="mx-auto flex h-full min-w-0 items-center rounded-md"
      >
        {collapsed ? (
          /* A arte original é para fundo escuro: no trilho vai sempre a invertida. */
          <BrandMark size={30} inverted />
        ) : (
          /* O nome do sistema, grande e vivo (ver `TechText`). */
          <TechText
            text="Obras Hoje"
            fontSize={32}
            letterSpacing={0.03}
            strokeWidth={0.75}
            dashLength={4}
            dashGap={3}
            specks={24}
          />
        )}
      </Link>

      {onClose ? (
        <button
          type="button"
          onClick={onClose}
          aria-label="Fechar menu"
          className="grid size-9 shrink-0 place-items-center rounded-md text-rail-muted transition-colors hover:bg-rail-2 hover:text-rail-ink"
        >
          <X {...ICON} size={18} aria-hidden />
        </button>
      ) : null}
    </div>
  );
}

/**
 * Casca do sistema: o trilho à esquerda, a barra superior no celular e a
 * transição de página em volta do conteúdo.
 *
 * O trilho é um painel escuro, flutuante — com folga da borda da janela e os
 * cantos do cartão —, igual nos dois temas. Recolhe a 68px e guarda a escolha
 * no navegador; recolhido, cada item mostra o rótulo numa dica ao lado.
 *
 * Vive na rota-mãe (ver `router.tsx`), então não remonta a cada navegação: o
 * indicador do item ativo desliza, e os contadores não são relidos.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [collapsed, setCollapsed] = useStoredState<boolean>(
    'obras:trilho-recolhido',
    false,
    (raw) => (typeof raw === 'boolean' ? raw : undefined),
  );
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const telaCheia = useMatches({
    select: (matches) => matches.some((match) => match.staticData.telaCheia === true),
  });
  const { inPlatform } = useAuth();

  const home = inPlatform ? '/plataforma' : '/';
  const width = collapsed ? RAIL_WIDTH_COLLAPSED : RAIL_WIDTH;

  // A gaveta não sobrevive a alargar a janela: do `lg` em diante o trilho assume.
  useEffect(() => {
    if (!drawerOpen) return;
    const desktop = window.matchMedia('(min-width: 1024px)');
    const close = () => {
      if (desktop.matches) setDrawerOpen(false);
    };
    desktop.addEventListener('change', close);
    return () => desktop.removeEventListener('change', close);
  }, [drawerOpen]);

  return (
    /* No papel a navegação não existe: sobra a página inteira para o conteúdo. */
    <div
      className="min-h-dvh bg-bg transition-[padding-left] duration-240 ease-[var(--ease-out-quint)] lg:pl-[var(--trilho)] print:pl-0"
      style={{ '--trilho': `${width}px` } as CSSProperties}
    >
      <motion.aside
        initial={false}
        animate={{ width }}
        transition={TRANSITION.base}
        style={{ width }}
        className="on-dark fixed inset-y-0 left-0 z-40 hidden flex-col bg-rail text-rail-ink lg:flex print:hidden"
      >
        <RailHandle collapsed={collapsed} onToggle={() => setCollapsed((value) => !value)} />
        <RailHeader collapsed={collapsed} home={home} />

        <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto">
          <SidebarNav collapsed={collapsed} />
        </div>

        {/* Área do usuário: quem é, onde está e em que luz trabalha. */}
        {collapsed ? (
          <div className="flex shrink-0 flex-col items-center gap-2 py-3">
            <ThemeToggle orientation="vertical" />
            <AccountMenu placement="rail-collapsed" />
          </div>
        ) : (
          <div className="flex shrink-0 items-center gap-1.5 p-3">
            <AccountMenu placement="rail" />
            <ThemeToggle />
          </div>
        )}
      </motion.aside>

      <header className="sticky top-0 z-30 flex h-14 items-center gap-2 border-b border-line bg-surface px-3 lg:hidden print:hidden">
        <button
          type="button"
          onClick={() => setDrawerOpen(true)}
          aria-label="Abrir menu"
          className="grid size-10 place-items-center rounded-md text-ink-3 transition-colors hover:bg-sunken hover:text-ink"
        >
          <Menu {...ICON} size={20} aria-hidden />
        </button>

        <Link to={home} className="min-w-0 rounded-md" aria-label="Início">
          <BrandLockup size={24} />
        </Link>

        <div className="ml-auto">
          <AccountMenu placement="topbar" />
        </div>
      </header>

      <AnimatePresence>
        {drawerOpen ? (
          <>
            <motion.div
              key="tampa"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={TRANSITION.fast}
              onClick={() => setDrawerOpen(false)}
              className="fixed inset-0 z-50 bg-scrim lg:hidden"
              aria-hidden
            />
            <motion.aside
              key="gaveta"
              initial={{ x: -320 }}
              animate={{ x: 0 }}
              exit={{ x: -320 }}
              transition={TRANSITION.base}
              className="on-dark fixed inset-y-0 left-0 z-50 flex w-[280px] flex-col bg-rail text-rail-ink lg:hidden"
            >
              <RailHeader collapsed={false} home={home} onClose={() => setDrawerOpen(false)} />
              <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto">
                <SidebarNav onNavigate={() => setDrawerOpen(false)} />
              </div>
              <div className="flex shrink-0 items-center gap-1.5 p-3">
                <AccountMenu placement="rail" />
                <ThemeToggle />
              </div>
            </motion.aside>
          </>
        ) : null}
      </AnimatePresence>

      <main className="min-w-0">
        {telaCheia ? (
          /*
           * Tela cheia (o mapa): a página recebe a área de conteúdo inteira —
           * colada no trilho, na borda direita e no pé da janela — e da altura
           * dela no computador. No celular cresce com o conteúdo, abaixo da
           * barra superior de 56px.
           */
          <PageTransition
            pathname={pathname}
            subir={false}
            className="flex min-h-[calc(100dvh-3.5rem)] flex-col lg:h-dvh lg:min-h-0"
          >
            {children}
          </PageTransition>
        ) : (
          <div className="mx-auto max-w-[1400px] px-4 py-6 lg:px-8 lg:py-9 print:max-w-none print:px-2 print:py-0">
            <PageTransition pathname={pathname}>{children}</PageTransition>
          </div>
        )}
      </main>
    </div>
  );
}

export function PageHeader({
  title,
  description,
  action,
  breadcrumb,
}: {
  title: string;
  description?: string | undefined;
  action?: ReactNode | undefined;
  breadcrumb?: ReactNode | undefined;
}) {
  return (
    <div className="mb-6">
      {breadcrumb ? <div className="mb-2.5 text-[13px] text-ink-3">{breadcrumb}</div> : null}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-[26px] font-semibold tracking-tight">{title}</h1>
          {description ? (
            <p className="mt-1 max-w-[68ch] text-[14px] text-ink-3">{description}</p>
          ) : null}
        </div>
        {action}
      </div>
    </div>
  );
}
